import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CHAT_MESSAGE_TYPE } from '@medflex/utils/constants';
import isPatientUserId from '@medflex/utils/isPatientUserId';
import { Sources } from '@medflex/shared-types';
import _ from 'lodash/fp';
import getFullNameWithTitle from '@medflex/utils/getFullNameWithTitle';
import useUserProfile from './useUserProfile';
import useChatContacts from './useChatContacts';

const usePamRequests = chatIds => {
  const dispatch = useDispatch();
  const { userProfile } = useUserProfile();
  const { contacts } = useChatContacts();
  const { load } = dispatch.pamRequests;
  const pamRequestsByChatId = useSelector(state => state.pamRequests);

  useEffect(() => {
    if (chatIds.length) {
      load({ chatIds });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(chatIds), load]);

  const requestMessagesByChatId = useMemo(() => {
    return Object.keys(pamRequestsByChatId)
      .filter(key => chatIds.includes(key))
      .reduce((obj, key) => {
        if (!pamRequestsByChatId[key].subscription?.active) {
          // eslint-disable-next-line no-param-reassign
          obj[key] = {
            requests: [],
            subscription: pamRequestsByChatId[key].subscription,
          };
          return obj;
        }
        const requestMessages = pamRequestsByChatId[key].requests.map(pamRequest => {
          // Only the patient can be the sender with the exception of "PRACTICE" source requests
          const isSender =
            pamRequest.source !== Sources.PRACTICE ? isPatientUserId(userProfile.id) : true;
          let senderData = {};
          if (isSender && !isPatientUserId(userProfile.id)) {
            senderData = _.pick(
              ['id', 'firstName', 'lastName', 'initials', 'title', 'workArea1', 'jobTitle'],
              userProfile.attributes,
            );
            senderData.fullNameWithTitle = getFullNameWithTitle(senderData);
            senderData.jobTitle = senderData.workArea1 || senderData.jobTitle;
          } else if (isSender) {
            senderData = userProfile.attributes;
            senderData.fullNameWithTitle = [senderData.lastName, senderData.firstName].join(', ');
          } else {
            senderData = contacts.find(({ id }) => id === key).user;
          }

          return {
            data: pamRequest,
            id: pamRequest.id,
            isSender,
            isPatient: isSender,
            seen: !!pamRequest.readAt,
            sender: senderData,
            time: pamRequest.createdAt,
            type: CHAT_MESSAGE_TYPE.PAM_REQUEST,
            user: senderData,
          };
        });
        // eslint-disable-next-line no-param-reassign
        obj[key] = {
          requests: requestMessages,
          subscription: pamRequestsByChatId[key].subscription,
        };
        return obj;
      }, {});
  }, [chatIds, contacts, pamRequestsByChatId, userProfile.attributes, userProfile.id]);

  return requestMessagesByChatId;
};

export default usePamRequests;
